import React, { useRef, useEffect } from 'react';
import { X, Eye } from 'lucide-react';
import { CalendarEvent, statusConfig, assetTypeConfig } from './types';
import { formatDateWithOptions, formatDisplayDate } from '@/utils/Date';

const getLocalDateString = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dayVal = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dayVal}`;
};

interface DayEventsModalProps {
  isOpen: boolean;
  date?: Date;
  events?: CalendarEvent[];
  onClose: () => void;
  onEventClick: (event: CalendarEvent) => void;
  showStatusLabel?: boolean;
  showAssetTypeLabel?: boolean;
}

const DayEventsModal: React.FC<DayEventsModalProps> = ({
  isOpen,
  date,
  events,
  onClose,
  onEventClick,
  showStatusLabel = true,
  showAssetTypeLabel = true,
}) => {
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const firstEventButtonRef = useRef<HTMLButtonElement>(null);
  const dedupedEvents = React.useMemo(() => {
    if (!events) return [];

    const map = new Map<string, CalendarEvent>();

    events.forEach((ev) => {
      const isBlockedBerth = ev.isBlocked && ev.assetType === 'berth';

      if (!isBlockedBerth) {
        const key = `${ev.assetType || 'berth'}-${ev.id}`;
        if (!map.has(key)) {
          map.set(key, ev);
        }
        return;
      }

      const berthKey = `blocked-berth-${ev.berth?.id ?? ev.berthNumber}`;
      const existing = map.get(berthKey);

      if (!existing) {
        map.set(berthKey, ev);
        return;
      }

      const getRangeLength = (event: CalendarEvent) => {
        const startTime = new Date(event.start).getTime();
        const endTime = new Date(event.end ?? event.start).getTime();
        if (isNaN(startTime) || isNaN(endTime)) return 0;
        return endTime - startTime;
      };

      const existingLength = getRangeLength(existing);
      const currentLength = getRangeLength(ev);

      if (currentLength > existingLength) {
        map.set(berthKey, ev);
      }
    });

    return Array.from(map.values());
  }, [events]);

  // Focus management
  useEffect(() => {
    if (isOpen) {
      let attempts = 0;
      let timer: NodeJS.Timeout;

      const tryFocus = () => {
        // Try to focus the first event button if available, otherwise the close button
        const targetElement =
          firstEventButtonRef.current || closeButtonRef.current;

        if (targetElement && attempts < 10) {
          targetElement.focus();
          // Check if focus was successful
          if (document.activeElement === targetElement) {
            return; // Success, stop trying
          }
          attempts++;
          timer = setTimeout(tryFocus, 100);
        }
      };

      // Start trying to focus after a short delay
      timer = setTimeout(tryFocus, 100);

      return () => {
        if (timer) clearTimeout(timer);
      };
    }
  }, [isOpen]);

  // Handle Escape key
  useEffect(() => {
    if (isOpen) {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          onClose();
        }
      };

      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [isOpen, onClose]);

  const sorted = [...dedupedEvents].sort((a, b) =>
    a.title.localeCompare(b.title)
  );

  // Count the number of available berths and regular bookings
  const availableCount = sorted.filter((ev) => ev.isAvailable).length;
  const bookingsCount = sorted.length - availableCount;

  // Group events by asset type
  const groupedEvents = sorted.reduce(
    (acc, event) => {
      const assetType =
        event.assetType || (event.service ? 'service' : 'berth');
      if (!acc[assetType]) {
        acc[assetType] = [];
      }
      acc[assetType].push(event);
      return acc;
    },
    {} as Record<string, CalendarEvent[]>
  );

  // Get all unique asset types
  const assetTypes = Object.keys(groupedEvents);

  // Only render when the modal is open and we have a date
  if (!isOpen || !date) return null;

  const dateLabel = formatDateWithOptions(
    date,
    {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    },
    undefined
  );

  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center p-4 bg-black/50">
      <div className="bg-gray-100 w-full max-w-lg rounded-xl shadow-xl max-h-[80vh] flex flex-col">
        <div className="p-5 border-b flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {availableCount > 0 && bookingsCount > 0
                ? `Bookings & Available Assets (${sorted.length})`
                : availableCount > 0
                  ? `Available Assets (${sorted.length})`
                  : `Bookings (${sorted.length})`}
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">{dateLabel}</p>
          </div>
          <button
            ref={closeButtonRef}
            onClick={onClose}
            aria-label="Close"
            className="p-1.5 rounded-md hover:bg-gray-100"
          >
            <X className="w-5 h-5" aria-hidden="true" />
          </button>
        </div>
        <div
          className="custom-scrollbar max-h-[500px] overflow-y-auto p-4"
          tabIndex={0}
          aria-label="Day bookings and available assets"
        >
          {sorted.length === 0 && (
            <div className="py-6 text-sm text-gray-500 text-center">
              No bookings or available assets.
            </div>
          )}

          {assetTypes.map((assetType) => (
            <React.Fragment key={assetType}>
              {/* Asset Type Heading */}
              {showAssetTypeLabel && assetTypes.length > 1 && (
                <div className="mb-2 mt-3 first:mt-0">
                  <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <span
                      className={`w-2 h-2 rounded-full ${assetTypeConfig[assetType as keyof typeof assetTypeConfig]?.color ?? 'bg-gray-300'} mr-2`}
                    ></span>
                    {
                      assetTypeConfig[assetType as keyof typeof assetTypeConfig]
                        ?.label
                    }
                  </h4>
                </div>
              )}

              {groupedEvents[assetType].map((ev) => {
                const cfg = statusConfig[ev.status];
                const isConflict = ev.isConflicting;
                const isAvailable = ev.isAvailable;
                const assetTypeCfg =
                  assetTypeConfig[
                    ev.assetType || (ev.service ? 'service' : 'berth')
                  ];

                return (
                  <button
                    ref={
                      groupedEvents[assetType][0] === ev
                        ? firstEventButtonRef
                        : undefined
                    }
                    key={ev.id}
                    onClick={() => onEventClick(ev)}
                    className={`
                      group w-full text-left py-3 flex gap-3 items-start px-2 rounded-md transition relative 
                      border mb-2 hover:brightness-95 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500
                      ${isAvailable ? cfg.pattern : cfg.lightColor}
                      ${isAvailable ? 'bg-green-100 dark:bg-green-900 contrast:bg-green-900' : ''}
                      ${isConflict ? 'border-red-600 ring-1 ring-red-400/60' : isAvailable ? 'border-green-500' : cfg.borderColor}
                      ${isConflict ? 'before:absolute before:inset-0 before:bg-[repeating-linear-gradient(45deg,rgba(255,0,0,0.08)_0,rgba(255,0,0,0.08)_6px,transparent_6px,transparent_12px)] before:rounded-md before:z-0' : ''}
                      relative
                    `}
                  >
                    <span
                      className={`w-3 h-3 mt-2 rounded-full ${isConflict ? 'bg-red-600' : isAvailable ? 'bg-green-500 ring-1 ring-green-300' : cfg.color} z-10 relative`}
                    ></span>
                    <div className="flex-1 min-w-0 pr-6 z-10 relative">
                      <p
                        className={`text-sm font-medium truncate ${cfg.textColor}`}
                      >
                        {ev.title}
                      </p>
                      <p className="text-xs text-gray-600 contrast:text-gray-900 truncate flex items-center gap-1 flex-wrap">
                        {showStatusLabel ? cfg.label : null}
                        {ev.berthNumber &&
                          ev.assetType === 'berth' &&
                          ` • Berth ${ev.berthNumber}`}

                        {/* Asset Type Badge */}
                        {showAssetTypeLabel ? (
                          <span
                            className={`inline-block px-1.5 py-0.5 text-[10px] font-medium ${assetTypeCfg.lightColor} ${assetTypeCfg.textColor} rounded ml-1`}
                            title={`Asset Type: ${assetTypeCfg.label}`}
                          >
                            {assetTypeCfg.label}
                          </span>
                        ) : null}

                        {isConflict && (
                          <span className="inline-block px-1.5 py-0.5 text-[10px] font-semibold bg-red-600 text-white rounded ml-1">
                            Conflict
                          </span>
                        )}
                        {isAvailable && (
                          <span className="inline-block px-1.5 py-0.5 text-[10px] font-semibold bg-green-700 contrast:bg-green-800 text-white rounded ml-1">
                            Available
                          </span>
                        )}
                      </p>
                      <p className="text-[11px] text-gray-500 truncate">
                        {formatDisplayDate(new Date(ev.start))} –{' '}
                        {ev.end
                          ? formatDisplayDate(new Date(ev.end))
                          : 'Ongoing'}
                        {ev.assetType === 'berth' &&
                          !ev.isAvailable &&
                          !ev.isBlocked &&
                          ev.end === getLocalDateString(date) && (
                            <span className="text-blue-600 dark:text-blue-400 contrast:text-blue-400 font-semibold ml-1.5">
                              (Checkout)
                            </span>
                          )}
                      </p>
                    </div>
                    <Eye
                      className="w-4 h-4 mt-1 text-gray-600 flex-shrink-0 opacity-100 translate-x-0 transition-all duration-150 z-10 relative"
                      aria-hidden="true"
                    />
                    <span className="sr-only">View booking</span>
                  </button>
                );
              })}
            </React.Fragment>
          ))}
        </div>
        <div className="p-4 border-t flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-200"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default DayEventsModal;
